import { MenuItem, MenuList, Select } from '@material-ui/core';
import clsx from 'clsx';
import React, { useCallback, useState } from 'react';
import { Card } from '../Card/Card';
import { CollapseButton } from '../CollapseButton/CollapseButton';
import { Dialog } from '../Dialog/Dialog';
import { Typography } from '../Typography/Typography';
import { useIsMobile } from '../utils/useIsMobile';
import { DropdownIconIndicator } from '../form/Dropdown/components/DropdownIconIndicator';

import useStyles from './Select.style';

const useSelect = (defaultValue: any) => {
  const [value, setValue] = React.useState(defaultValue);
  const handleSelectChange = React.useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
    setValue(event.target.value as any);
  }, []);

  return [value, handleSelectChange] as const;
};

export interface CustomSelectProps {
  options: (string | number)[];
  onChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  value?: number | string;
  labelId: string;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  className?: string;
  classNameIcon?: string;
  autoTestId?: string;
  variant?: 'standard' | 'outlined';
  collapseButtonSize?: 'medium';
}
const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  onChange,
  value,
  labelId,
  open,
  onClose,
  onOpen,
  className,
  classNameIcon,
  autoTestId,
  variant = "standard",
  collapseButtonSize,
}) => {
  const c = useStyles({ variant });
  const isMobile = useIsMobile();
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  const [isMobileDialogOpen, setIsMobileDialogOpen] = useState(false);
  const isMenuOpen = open ?? isMobileDialogOpen;

  const handleMobileOpen = () => {
    setIsAccordionExpanded(true);
    setIsMobileDialogOpen(true);
    onOpen?.();
  };

  const handleMobileClose = () => {
    setIsAccordionExpanded(false);
    setIsMobileDialogOpen(false);
    onClose?.();
  };

  const handleMobileItemClick = (option: string | number) => {
    onChange({ target: { value: option } } as React.ChangeEvent<{ value: unknown }>);
    handleMobileClose();
  };

  const handleOpen = () => {
    setIsAccordionExpanded(true);
    onOpen?.();
  };

  const handleClose = () => {
    setIsAccordionExpanded(false);
    onClose?.();
  };

  const renderMenuItems = () => {
    return options.map((option: string | number) => (
      <MenuItem
        key={option}
        value={option}
        className={c.menuItem}
        onClick={isMobile ? () => handleMobileItemClick(option) : undefined}
      >
        <div className={c.menuItemContent}>
          <Typography component="span" textColor="greys.80">{option}</Typography>
        </div>
      </MenuItem>
    ));
  };

  const IconComponent = useCallback(
    (props) => {
      if (variant !== "outlined") return null;
      return <DropdownIconIndicator {...props} />;
    },
    [variant],
  );

  if (isMobile && variant === "standard") {
    return (
      <div className={clsx(c.root, className)}>
        <CollapseButton
          onClick={handleMobileOpen}
          expanded={isAccordionExpanded}
          testId={'collapse-button'}
          size={collapseButtonSize}
        >
          {value}
        </CollapseButton>

        <Dialog
          closeHandler={handleMobileClose}
          open={isMenuOpen}
          className={c.dialog}
        >
          <Card className={c.dialogCard}>
            <MenuList className={c.menuListMobile}>
              {renderMenuItems()}
            </MenuList>
          </Card>
        </Dialog>
      </div>
    );
  }


  return (
    <Select
      IconComponent={IconComponent}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      data-testid="select"
      data-autotestid={autoTestId}
      className={clsx(c.root, className)}
      classes={{
        root: c.select,
        icon: clsx(c.icon, classNameIcon),
      }}
      renderValue={(selected) =>
        variant === "standard" ? (
          <CollapseButton
            onClick={() => setIsAccordionExpanded((s) => !s)}
            expanded={isAccordionExpanded}
            size={collapseButtonSize}
          >
            {selected}
          </CollapseButton>
        ) : <Typography component="span" textColor="greys.80">{selected}</Typography>
      }
      labelId={labelId}
      value={value}
      onChange={onChange}
      MenuProps={{
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
        getContentAnchorEl: null,
        classes: { list: c.menuList },
        PopoverClasses: {
          paper: c.popover,
        },
      }}
      variant={variant}
    >
      {renderMenuItems()}
    </Select>
  );
};

export { CustomSelect as Select, useSelect };
